import {
  Checkbox,
  createStyles,
  IconButton,
  makeStyles,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Group, Person, Publish, Assignment } from '@material-ui/icons';
import GetAppIcon from '@material-ui/icons/GetApp';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import FolderIcon from '@material-ui/icons/Folder';
import React, { useEffect, useRef, useState } from 'react';
import { BimTreeNode, FileNode, FileNodeTypeEnum } from '../../api-client/autogenerated';
import {
  areFilesInSameParentFolder,
  formatDate,
  formatFileSize,
  isPublicPage,
  openFilesystemFileInNewTab,
  openPublicFilesystemFileInNewTab,
  PublicDesignMatchParams,
} from '../../scripts/utils';
import EyeIcon from '@material-ui/icons/Visibility';
import Button from '@material-ui/core/Button';
import FileLogDialog from './FileLogDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareSquare } from '@fortawesome/free-solid-svg-icons';
import DriveShareDialog from './DriveShareDialog';
import { useParams } from 'react-router';
import { useRenameContext } from './context/RenameContext';
import { isRenameJobRunning } from './context/utils/renameJobHelpers';
import { useDialogState } from './context/DialogStateContext';
import { useSelector } from 'react-redux';
import { getProjectIsArchived } from '../../features/project/selectors';
import { ARCHIVED_PROJECT_TOOLTIP } from '../../scripts/permission-utils';
import CircularLoader from '../loader/CircularLoader';

interface FileTableRowProps {
  type: 'Project Files' | 'BIM360';
  disableOptions: boolean;
  name: string;
  date: string;
  size: number;
  checked: boolean;
  file?: FileNode;
  bimFile?: BimTreeNode;
  projectId: string | undefined;
  downloadFileFromBIM: (file: BimTreeNode) => Promise<void>;
  downloadFile: (name: string, fullKey: string, ownerId: string) => Promise<void>;
  updateFileName: (name: string, file: FileNode) => void;
  deleteFile: (file: FileNode) => void;
  importFromBIM?: boolean;
  importBIMFile: (file: BimTreeNode) => void;
  updateFileInActiveFolder?: (f: FileNode) => void;
  updateFolderName: (name: string, file: FileNode) => void;
  updateFileNode: (node: FileNode) => void;
  handleSelectRow: (fileNode: FileNode) => void;
  setTable: (a: any) => void;
  setActiveFolder: (fileNode: FileNode | undefined) => void;
  findParentFolder: (fullKey: string) => FileNode | undefined;
  manageParentFolder: (fullKey: string) => void;
  selectedNodes: FileNode[];
  allowOnlyPdfs?: boolean;
  /**
   * Single panel layout used on small screens: only the checkbox, name, date modified, size and
   * options columns are rendered, and a tap on the row opens a folder (there is no Open Folder
   * button to press).
   */
  mobile?: boolean;
  /**
   * A picker rather than the Drive itself: the only thing a row offers is being chosen. Every
   * action that acts on the file where it lives -- viewing, downloading, sharing, its activity
   * log, its options menu -- is gone along with its column, and folders are opened rather than
   * picked, so they carry no checkbox.
   */
  selectionOnly?: boolean;
  isRecycleBin?: boolean;
  disableInteraction?: boolean;
  isRenaming?: boolean;
  onOpenFileOptions?: (
    file: FileNode,
    anchorEl: HTMLElement | null,
    anchorPoint?: { top: number; left: number },
  ) => void;
  onRegisterRenameOpener?: (key: string, opener: () => void) => void;
  onUnregisterRenameOpener?: (key: string) => void;
  onManagePermissions?: (
    file: FileNode,
    anchorEl: HTMLElement | null,
    anchorPoint?: { top: number; left: number },
  ) => void;
}

const useStyles = makeStyles(
  createStyles({
    root: {
      height: 'auto',
      flexGrow: 1,
      maxWidth: 310,
      width: 310,
      alignSelf: 'flex-start',
      backgroundColor: 'white',
      maxHeight: '100%',
      overflowX: 'auto',
      overflowY: 'auto',
    },
    boxRoot: {
      backgroundColor: 'white',
    },
    rowLayout: {
      width: 'auto',
      display: 'flex',
      flexDirection: 'row',
      flexBasis: 'auto',
      flexGrow: 1,
    },
    table: {
      flexGrow: 1,
    },
    centerIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      flexBasis: 'auto',
      flexWrap: 'nowrap',
      height: '100%',
    },
    downloadIcon: {
      color: 'rgba(0, 0, 0, 0.54);',
      cursor: 'pointer',
    },
    inlineCenter: {
      display: 'flex',
      flexBasis: 'auto',
      flexWrap: 'nowrap',
      justifyContent: 'center',
      height: 48,
    },
    loadingIcon: {
      width: 32,
      height: 32,
    },
    sizeCell: {
      minWidth: 90,
    },
    smallSizeCell: {
      minWidth: 60,
    },
    row: {
      transition: '0.12s',
      height: 48,
    },
    tableBody: {
      '&>:nth-child(odd)': {
        backgroundColor: '#F9F9F9',
        '&:hover': {
          backgroundColor: '#E6EEFF',
        },
      },
      '&>:nth-child(even)': {
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#E6EEFF',
        },
      },
    },
    titleStyle: {
      background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
      borderRadius: '4px 4px 0px 0px',
      flexGrow: 1,
      padding: 16,
    },
    addButton: {
      width: 108,
      height: 79,
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
      background: '#0947B9',
      transition: '0.2s',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '&:hover': {
        background: '#4380ED',
      },
    },
    tableCell: {
      padding: '0px 16px 0px 16px',
    },
    mobileTableCell: {
      padding: '0px 8px 0px 8px',
    },
    folderRowIcon: {
      color: 'rgba(0, 0, 0, 0.54)',
      fontSize: 20,
      flexShrink: 0,
    },
    renaming: {
      pointerEvents: 'none',
    },
  }),
);

export default function FileTableRow(props: FileTableRowProps) {
  const {
    type,
    disableOptions,
    name,
    date,
    size,
    checked,
    file,
    bimFile,
    projectId,
    downloadFileFromBIM,
    downloadFile,
    updateFileName,
    deleteFile,
    importFromBIM,
    importBIMFile,
    updateFileInActiveFolder = () => {},
    updateFileNode,
    handleSelectRow,
    setTable,
    setActiveFolder,
    findParentFolder,
    manageParentFolder,
    updateFolderName,
    selectedNodes,
    allowOnlyPdfs,
    mobile,
    selectionOnly,
    isRecycleBin,
    disableInteraction,
    isRenaming,
    onOpenFileOptions,
    onRegisterRenameOpener,
    onUnregisterRenameOpener,
    onManagePermissions,
  } = props;

  const classes = useStyles();
  const publicParams = useParams<PublicDesignMatchParams>();

  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLElement | null>(null);

  const rightClickAnchor = useRef<HTMLDivElement | null>(null);
  const [optionsAnchor, setOptionsAnchor] = useState<null | HTMLElement>(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const canNotify = areFilesInSameParentFolder(selectedNodes);

  // const { open: openDialog, isOpen } = useDialogState();

  useEffect(() => {
    const handleCtxMenuGlobal = () => setOptionsAnchor(null);
    document.addEventListener('contextmenu', handleCtxMenuGlobal);

    return () => {
      document.removeEventListener('contextmenu', handleCtxMenuGlobal);
    };
  }, []);

  const [localFileNode, setLocalFileNode] = useState<FileNode | undefined>(file);
  const isFolder = localFileNode?.type === FileNodeTypeEnum.Folder;

  const [logDialogOpen, setLogDialogOpen] = useState<boolean>(false);
  const showView = type !== 'BIM360' && localFileNode?.file?.extension === 'pdf';

  const { state: renameState } = useRenameContext();
  const isArchivedProject = useSelector(getProjectIsArchived);

  const [selfRenaming, setSelfRenaming] = useState(false);
  const selfRenameKeyRef = useRef<string | null>(null);
  const normalizeKey = (k: string) => (k.endsWith('/') ? k : `${k}/`);

  // const isFileKey = (k: string) => {
  //   const parts = k.split('/').filter(Boolean);
  //   const last = parts[parts.length - 1] || '';
  //   return last.includes('.'); // crude but works for your keys
  // };

  const findMatchingRenameJob = (
    fileKey: string | undefined,
    renameState: Record<string, { percent?: number; status?: any }>,
  ) => {
    if (!fileKey) return null;
    const fk = normalizeKey(fileKey);

    // pick the longest matching running job whose key is a prefix of this file
    let best: [string, any] | null = null;
    for (const [k, job] of Object.entries(renameState)) {
      const rk = normalizeKey(k);
      if (!isRenameJobRunning(job)) continue;
      if (fk.startsWith(rk)) {
        if (!best || rk.length > normalizeKey(best[0]).length) {
          best = [k, job];
        }
      }
    }
    return best;
  };

  const getRowPercent = (
    fileKey: string | undefined,
    renameState: Record<string, { percent?: number; status?: any }>,
  ): number | undefined => {
    const match = findMatchingRenameJob(fileKey, renameState);
    return match?.[1]?.percent; // 0..100 (as provided by startPollingRenameJob)
  };

  // Match a running job for THIS row key
  const rowJobEntry = findMatchingRenameJob(localFileNode?.fullKey, renameState);
  const rowJob = rowJobEntry?.[1];
  const matchedKey = rowJobEntry?.[0] ? normalizeKey(rowJobEntry[0]) : null;
  const rowKey = normalizeKey(localFileNode?.fullKey || '');
  const isExactMatch = matchedKey === rowKey;

  // Only treat this row as “renaming” if the job is running AND it’s an exact-key match
  const rowIsRenamingExact = !!rowJob && isRenameJobRunning(rowJob) && isExactMatch;

  // Only show percent when this exact row is the one being renamed
  const filePercent = isExactMatch ? getRowPercent(localFileNode?.fullKey, renameState) : undefined;

  // Show indicator if we just kicked off this row’s rename (selfRenaming) or there’s an exact match
  const showSelfRenameIndicator = selfRenaming || rowIsRenamingExact;

  // Only disable this row if it’s the exact thing being renamed (or already disabled via props)
  const selfDisabled = rowIsRenamingExact || disableInteraction;

  // anchor for the FileOptionsDialog
  const globalAnchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.style.position = 'fixed';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.top = '0px';
    el.style.left = '0px';
    el.style.pointerEvents = 'none'; // safety55555
    document.body.appendChild(el);
    globalAnchorRef.current = el;

    return () => {
      document.body.removeChild(el);
      globalAnchorRef.current = null;
    };
  }, []);

  useEffect(() => {
    setLocalFileNode(file);
  }, [file]);

  useEffect(() => {
    if (!selfRenaming) return;
    const k = selfRenameKeyRef.current;
    if (!k) return;

    const job = renameState[k];

    if (!isRenameJobRunning(job)) {
      setSelfRenaming(false);
      selfRenameKeyRef.current = null;
    }
  }, [renameState, selfRenaming]);

  const handleOpenOptions = (
    event: React.MouseEvent<HTMLButtonElement>,
    file: FileNode | undefined,
  ) => {
    event.stopPropagation();
    if (file && onOpenFileOptions) {
      onOpenFileOptions(file, event.currentTarget, { top: event.clientY, left: event.clientX });
    }
  };

  // const handleDelete = () => {
  //   deleteFile(localFileNode!);
  // };

  const handleFinishEdit = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditing(false);
    if (localFileNode?.type === FileNodeTypeEnum.File) {
      const key = localFileNode.fullKey || '';
      selfRenameKeyRef.current = normalizeKey(key);
      setSelfRenaming(true);
      updateFileName(e.target.value, localFileNode!);
    } else updateFolderName(e.target.value, localFileNode!);
  };

  const keypressHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      inputRef!.current!.blur();
    }
  };

  const handleView = () => {
    if (isPublicPage())
      openPublicFilesystemFileInNewTab(
        projectId!,
        localFileNode!.file!.fullName!,
        localFileNode!.fullKey,
        publicParams.designFileShareId,
        publicParams.publicAccessKey,
      );
    else
      openFilesystemFileInNewTab(
        projectId!,
        localFileNode!.file!.fullName!,
        localFileNode!.fullKey,
        localFileNode!.ownerId,
        isRecycleBin,
      );
  };

  const handleDownload = async () =>
    type === 'BIM360'
      ? await downloadFileFromBIM(bimFile!)
      : await downloadFile(
          localFileNode!.file!.fullName!,
          localFileNode!.fullKey,
          localFileNode!.ownerId,
        );

  const handleFolderChange = () => {
    if (localFileNode?.type === FileNodeTypeEnum.Folder) {
      setTable(localFileNode.children);
      setActiveFolder(localFileNode);
    }
  };

  // Single panel layout has no per-row view icon (that whole column is dropped), so tapping the
  // row is the only way in: a folder opens it, a file opens it for viewing when that's possible
  // (PDFs only, same as the desktop eye icon's showView gate) -- anything else is a no-op, same as
  // tapping a non-PDF file's row does today.
  const handleRowClick = () => {
    if (isFolder) {
      handleFolderChange();
    } else if (showView) {
      handleView();
    }
  };

  const getActionIcon = () => {
    if (!importFromBIM) {
      if (!isFolder) {
        // A picker offers nothing to do with a file except choose it; the folder button below
        // stays, since it is the only way down into the tree from the table.
        if (selectionOnly) return null;
        return (
          <div style={{ display: 'flex', justifyContent: 'space-evenly', whiteSpace: 'nowrap' }}>
            {showView ? (
              <Tooltip arrow placement="top" title="View">
                <IconButton
                  className={classes.downloadIcon}
                  onClick={handleView}
                  disabled={disableInteraction}
                  style={{ padding: 0 }}
                >
                  <EyeIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <div style={{ width: '24px' }} />
            )}
            <Tooltip arrow placement="top" title="Download">
              <IconButton
                className={classes.downloadIcon}
                onClick={handleDownload}
                disabled={disableInteraction}
                style={{ padding: 0 }}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            {!isPublicPage() && !isRecycleBin ? (
              <Tooltip
                arrow
                placement="top"
                title={isArchivedProject ? ARCHIVED_PROJECT_TOOLTIP : 'Send'}
              >
                {/* Sharing notifies the file's users and emails a link to everyone else, both of
                    which write to the project's record. */}
                <span>
                  <IconButton
                    onClick={() => setShareDialogOpen(true)}
                    className={classes.downloadIcon}
                    disabled={disableInteraction || isArchivedProject}
                    style={{ padding: 0 }}
                  >
                    <FontAwesomeIcon icon={faShareSquare} size="xs" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </div>
        );
      }
      return (
        <Button color="primary" onClick={handleFolderChange} disabled={disableInteraction}>
          Open Folder
        </Button>
      );
    }

    return (
      <Tooltip
        arrow
        placement="top"
        title={isArchivedProject ? ARCHIVED_PROJECT_TOOLTIP : 'Import'}
      >
        {/* Importing copies the BIM file into the project. */}
        <span>
          <IconButton
            className={classes.downloadIcon}
            style={{ marginLeft: 8, marginRight: 8 }}
            size="small"
            onClick={() => {
              importBIMFile(bimFile!);
            }}
            disabled={disableInteraction || isArchivedProject}
          >
            <Publish />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const updateFileLocally = (f: FileNode) => {
    setLocalFileNode(f);
    updateFileInActiveFolder!(f);
  };

  const shareButtonPressed = (
    event: React.MouseEvent<HTMLButtonElement>,
    file: FileNode | undefined,
  ) => {
    if (!file || !onManagePermissions) return;

    onManagePermissions(file, null);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLTableRowElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!localFileNode || !onOpenFileOptions) return;

    onOpenFileOptions(localFileNode, null, { top: event.clientY + 24, left: event.clientX });
  };

  useEffect(() => {
    if (editing) {
      inputRef!.current!.focus();
    }
  }, [editing]);

  useEffect(() => {
    if (!localFileNode?.fullKey) return;
    if (!onRegisterRenameOpener) return;

    // The tree item normally registers the opener for a folder, but the single panel layout does
    // not render the tree -- so the row registers under the same trailing-slash-free key the
    // rename lookup uses. Only on mobile: on the two panel layout both would claim the same key.
    const key = mobile ? localFileNode.fullKey.replace(/\/$/, '') : localFileNode.fullKey;
    const opener = () => setEditing(true);

    onRegisterRenameOpener(key, opener);

    return () => {
      onUnregisterRenameOpener?.(key);
    };
  }, [localFileNode?.fullKey, mobile, onRegisterRenameOpener, onUnregisterRenameOpener]);

  return (
    <TableRow
      key={name}
      onContextMenu={!disableInteraction ? handleContextMenu : undefined}
      onDoubleClick={!disableInteraction ? handleFolderChange : undefined}
      onClick={mobile && !disableInteraction ? handleRowClick : undefined}
      className={!rowIsRenamingExact ? classes.row : `${classes.row} ${classes.renaming}`}
      style={{
        opacity: selfDisabled ? 0.5 : 1,
        cursor: selfDisabled ? 'not-allowed' : 'pointer',
      }}
    >
      <TableCell
        align="center"
        padding="none"
        onClick={mobile ? (event) => event.stopPropagation() : undefined}
      >
        {(allowOnlyPdfs !== true || (allowOnlyPdfs && showView)) &&
          !(selectionOnly && isFolder) && (
            <Checkbox
              checked={checked}
              onChange={() => handleSelectRow(localFileNode!)}
              disabled={selfDisabled}
              style={{ padding: '0px 0px 0px 8px' }}
            />
          )}
      </TableCell>

      {!mobile ? (
        <TableCell align="center" padding="none">
          {getActionIcon()}
        </TableCell>
      ) : null}
      {isRecycleBin ? (
        <TableCell
          classes={{ root: classes.tableCell }}
          style={{
            whiteSpace: 'nowrap',
            maxWidth: '320px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          component="th"
          scope="row"
        >
          {`/${file?.fullKey.split('/').slice(3, -1).join('/')}/`}
        </TableCell>
      ) : null}
      <TableCell
        classes={{ root: mobile ? classes.mobileTableCell : classes.tableCell }}
        style={{
          // A phone has no column to widen and no ellipsis worth reading -- half a filename
          // identifies nothing -- so the name wraps to as many lines as it needs. A cell that will
          // not wrap sets the table's min-content width, which is what put a horizontal scrollbar
          // under the whole file list. On desktop the column stays capped and clipped.
          whiteSpace: mobile ? 'normal' : 'nowrap',
          // Filenames carry no spaces to break at, so they are allowed to break anywhere.
          overflowWrap: mobile ? 'anywhere' : undefined,
          maxWidth: !mobile ? '320px' : undefined,
          overflow: !mobile ? 'hidden' : undefined,
          textOverflow: !mobile ? 'ellipsis' : undefined,
        }}
        component="th"
        scope="row"
        colSpan={!mobile && isFolder ? 3 : 1}
      >
        <div
          style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1 }}
          ref={rightClickAnchor}
        />
        {editing ? (
          <div style={{ width: '100%' }}>
            <TextField
              inputRef={inputRef}
              defaultValue={name.includes('.') ? name.substr(0, name.lastIndexOf('.')) : name}
              onBlur={(e) => handleFinishEdit(e)}
              onKeyPress={(event) => keypressHandler(event)}
              fullWidth
              style={{ maxWidth: 280 }}
            />
          </div>
        ) : (
          <>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                maxWidth: '100%',
              }}
            >
              {mobile && isFolder && <FolderIcon className={classes.folderRowIcon} />}
              {showSelfRenameIndicator ? (
                filePercent !== null ? (
                  <span style={{ fontSize: 12, opacity: 0.9 }}>
                    {Math.round(filePercent || 0)}%
                  </span>
                ) : (
                  <span style={{ fontSize: 12, opacity: 0.9 }}>[renaming]</span>
                )
              ) : null}
              <span
                style={{
                  overflow: mobile ? undefined : 'hidden',
                  textOverflow: mobile ? undefined : 'ellipsis',
                  whiteSpace: mobile ? 'normal' : 'nowrap',
                  overflowWrap: mobile ? 'anywhere' : undefined,
                  minWidth: 0,
                }}
                title={name}
              >
                {name}
              </span>
            </span>
            {mobile && !isFolder && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                <span style={{ fontSize: 12, color: '#7A797A', whiteSpace: 'nowrap' }}>
                  {formatDate(date, mobile)}
                </span>
                <span style={{ fontSize: 12, color: '#7A797A', whiteSpace: 'nowrap' }}>
                  {formatFileSize(size)}
                </span>
              </div>
            )}
          </>
        )}
      </TableCell>
      {!isFolder && !mobile && (
        <>
          <TableCell style={{ maxWidth: 160, whiteSpace: 'nowrap' }} align="left">
            {formatDate(date, mobile)}
          </TableCell>
          <TableCell
            align="left"
            classes={{ root: classes.tableCell }}
            className={classes.sizeCell}
          >
            {formatFileSize(size)}
          </TableCell>
        </>
      )}
      {!mobile && !isPublicPage() && !isRecycleBin && !selectionOnly ? (
        <TableCell
          align="center"
          classes={{ root: classes.tableCell }}
          className={classes.smallSizeCell}
        >
          {localFileNode?.fileNodePermissions && localFileNode?.fileNodePermissions.length === 0 ? (
            <IconButton
              onClick={(event) => shareButtonPressed(event, localFileNode)}
              disabled={disableInteraction}
            >
              <Person className={classes.downloadIcon} />
            </IconButton>
          ) : (
            <IconButton
              onClick={(event) => shareButtonPressed(event, localFileNode)}
              disabled={disableInteraction}
            >
              <Group className={classes.downloadIcon} />
            </IconButton>
          )}
        </TableCell>
      ) : null}

      {!mobile && !isPublicPage() && !selectionOnly ? (
        <TableCell align="center" classes={{ root: classes.tableCell }}>
          <IconButton onClick={() => setLogDialogOpen(true)} disabled={disableInteraction}>
            <Assignment className={classes.downloadIcon} />
          </IconButton>
        </TableCell>
      ) : null}

      {!selectionOnly ? (
        <TableCell
          align="center"
          classes={{ root: mobile ? classes.mobileTableCell : classes.tableCell }}
          onClick={mobile ? (event) => event.stopPropagation() : undefined}
        >
          {disableOptions ? null : (
            <IconButton
              className={classes.downloadIcon}
              size="small"
              onClick={(event) => handleOpenOptions(event, localFileNode)}
              disabled={disableInteraction}
            >
              <MoreHorizIcon />
            </IconButton>
          )}
        </TableCell>
      ) : null}
      {localFileNode && (
        <DriveShareDialog
          open={shareDialogOpen}
          files={selectedNodes.length > 0 ? selectedNodes : [localFileNode]}
          handleClose={() => setShareDialogOpen(false)}
          isRecycleBin={isRecycleBin}
        />
      )}
      {localFileNode && (
        <FileLogDialog
          open={logDialogOpen}
          handleClose={() => setLogDialogOpen(false)}
          file={localFileNode}
          isRecycleBin={isRecycleBin}
        />
      )}
    </TableRow>
  );
}
